
import React, { useEffect, useState, useRef } from 'react';
import { Package, Loader2, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Product, Category, CategoryDoc } from '../types';
import { ProductCardLoader } from './ProductCardLoader';
import { ProductCard } from './ProductCard';
import { OptimizedImage } from './OptimizedImage';
import { useProducts } from '../hooks/useFirestoreData';
import { doc, getDoc, db, collection, query, where, getDocs, onSnapshot } from '../firebase';
import { PageConfig } from '../types';

interface CategoryViewProps {
  activeCategory: Category | null;
  products: Product[]; // Kept for compatibility but we fetch fresh
  isDark: boolean;
  onOpenDetails: (product: Product) => void;
  onAddToCart: (e: React.MouseEvent, product: Product) => void;
  addedToCartMap: { [key: number]: boolean };
}

export const CategoryView: React.FC<CategoryViewProps> = ({
  activeCategory,
  isDark,
  onOpenDetails,
  onAddToCart,
  addedToCartMap
}) => {
  const { products, loading } = useProducts(activeCategory?.id);
  const [categoryDoc, setCategoryDoc] = useState<CategoryDoc | null>(null);
  const [pageConfig, setPageConfig] = useState<PageConfig | null>(null);
  const [categoryLoading, setCategoryLoading] = useState(true);

  // Fetch category document for banner and display settings
  useEffect(() => {
    const fetchCategoryDoc = async () => {
      if (!activeCategory?.id) {
        setCategoryLoading(false);
        return;
      }
      try {
        // Try to find category by matching name or ID
        // Since we're using numeric IDs in extendedCategories, we need to query by slug or name
        const categoriesRef = collection(db, 'categories');
        const q = query(categoriesRef, where('slug', '==', activeCategory.name.toLowerCase().replace(/\s+/g, '-')));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const catDoc = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as CategoryDoc;
          setCategoryDoc(catDoc);
        }
      } catch (err) {
        console.error("Error fetching category doc:", err);
      } finally {
        setCategoryLoading(false);
      }
    };
    fetchCategoryDoc();
  }, [activeCategory?.id, activeCategory?.name]);

  // Fetch PageConfig for dynamic builder sections
  useEffect(() => {
    if (!activeCategory?.id) return;
    const unsub = onSnapshot(doc(db, 'pageConfigs', `category_${activeCategory.id}`), (docSnap) => {
      if (docSnap.exists()) {
        setPageConfig(docSnap.data() as PageConfig);
      } else {
        setPageConfig(null);
      }
    });
    return unsub;
  }, [activeCategory?.id]);

  const activeBanners = pageConfig?.banners && pageConfig.banners.length > 0 ? pageConfig.banners : (categoryDoc?.banners || []);

  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [isDraggingBanner, setIsDraggingBanner] = useState(false);
  const bannerStartX = useRef(0);
  const bannerScrollLeft = useRef(0);
  const bannerSliderRef = useRef<HTMLDivElement>(null);

  // Auto-sliding logic for banners
  useEffect(() => {
    if (activeBanners.length <= 1 || isDraggingBanner) return;

    const timer = (categoryDoc?.displaySettings?.bannerTimer || 5) * 1000;
    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % activeBanners.length);
    }, timer);

    return () => clearInterval(interval);
  }, [activeBanners.length, categoryDoc?.displaySettings?.bannerTimer, isDraggingBanner]);

  const handleBannerDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (activeBanners.length <= 1) return;
    setIsDraggingBanner(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    bannerStartX.current = clientX;
  };

  const handleBannerDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDraggingBanner) return;
    // We could add live drag offset here if desired, but for now we'll just handle the end
  };

  const handleNextSlide = () => {
    setCurrentBannerIndex(prev => (prev + 1) % activeBanners.length);
  };

  const handlePrevSlide = () => {
    setCurrentBannerIndex(prev => (prev - 1 + activeBanners.length) % activeBanners.length);
  };

  const handleBannerDragEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDraggingBanner) return;
    setIsDraggingBanner(false);

    // Logic to determine swipe direction
    const endX = 'changedTouches' in e ? e.changedTouches[0].clientX : (e as any).clientX;
    const diff = bannerStartX.current - endX;

    if (Math.abs(diff) > 50) { // Threshold for swipe
      if (diff > 0) {
        // Swipe Left -> Next
        handleNextSlide();
      } else {
        // Swipe Right -> Prev
        handlePrevSlide();
      }
    }
  };

  // Apply display settings
  let displayProducts = products;
  if (categoryDoc?.displaySettings) {
    const settings = categoryDoc.displaySettings;

    // Sort products
    if (settings.productOrder) {
      displayProducts = [...products].sort((a, b) => {
        switch (settings.productOrder) {
          case 'newest':
            return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
          case 'oldest':
            return (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0);
          case 'price_asc':
            return a.price - b.price;
          case 'price_desc':
            return b.price - a.price;
          case 'rating':
            return b.rating - a.rating;
          default:
            return 0;
        }
      });
    }

    // Limit count
    if (settings.productCount) {
      displayProducts = displayProducts.slice(0, settings.productCount);
    }
  }

  // Filter special categories logic (Best Sellers, Offers) which might not map to DB IDs directly
  // For 'Best Sellers' (id 1) and 'Offers' (id 2), we might want client-side logic if DB doesn't support it directly
  // The hook does simple equality check. 
  // If id=1 (Best Sellers) or id=2 (Offers) are not actual categoryIds in DB, we rely on the passed `products` or special handling.
  // For this implementation, we assume basic category ID filtering via hook.

  // Note: If Category 1 and 2 are special, the backend/hook should handle it or we assume they exist in DB.

  const displayType = categoryDoc?.displaySettings?.scrollType || 'grid';
  const sectionTitle = categoryDoc?.displaySettings?.sectionTitle || activeCategory?.name;

  return (
    <div className="flex-1 flex flex-col items-center justify-start w-full px-0 sm:px-6 overflow-hidden min-h-0 pt-20 md:pt-28">
      <div className="w-full flex-1 min-h-0 max-w-[1400px] flex flex-col">
        {/* Banners Slider */}
        {activeBanners.length > 0 && (
          <div
            ref={bannerSliderRef}
            className="w-full mb-8 rounded-none sm:rounded-[1.5rem] md:rounded-[3rem] overflow-hidden relative aspect-[2/1] sm:aspect-[2.2/1] md:aspect-[2.4/1] border-b sm:border border-white/5 shadow-2xl bg-black/40 group/slider cursor-grab active:cursor-grabbing"
            onMouseDown={handleBannerDragStart}
            onMouseMove={handleBannerDragMove}
            onMouseUp={handleBannerDragEnd}
            onMouseLeave={handleBannerDragEnd}
            onTouchStart={handleBannerDragStart}
            onTouchMove={handleBannerDragMove}
            onTouchEnd={handleBannerDragEnd}
          >
            <div
              className={`w-full h-full flex transition-transform duration-700 ease-[cubic-bezier(0.23,1,0.32,1)] ${isDraggingBanner ? 'duration-0' : ''}`}
              style={{ transform: `translateX(-${currentBannerIndex * 100}%)`, direction: 'ltr' }}
            >
              {[...activeBanners].sort((a, b) => (a.order || 0) - (b.order || 0)).map((b: any, i) => {
                const titleStr = b.discount || b.title;
                const subTitleStr = b.subTitle;
                const btnStr = b.buttonText;
                const linkStr = b.buttonLink || b.link;
                return (
                  <div key={i} className="w-full h-full shrink-0 relative flex items-center justify-center bg-black">
                    <OptimizedImage
                      src={b.image}
                      alt=""
                      aspectRatio="2.4/1"
                      priority={i === currentBannerIndex}
                      className={`w-full h-full transition-transform duration-[1.5s] ease-out ${currentBannerIndex === i ? 'scale-100 opacity-100' : 'scale-110 opacity-40'}`}
                    />

                    <div className={`absolute inset-0 p-4 md:p-12 z-20 pointer-events-none transition-all duration-700 ${currentBannerIndex === i ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`} dir="rtl">
                      {/* Tag */}
                      {!b.hideSubTitle && subTitleStr && (
                        <span
                          className="absolute text-white/90 font-black text-[10px] sm:text-xs md:text-lg tracking-wider uppercase bg-white/10 px-2.5 py-1 rounded-lg backdrop-blur-md border border-white/20 whitespace-nowrap z-30 shadow-sm transition-all duration-700"
                          style={{
                            left: `${b.tagPosition?.x || 10}%`,
                            top: `${b.tagPosition?.y || 35}%`,
                            transform: 'translate(-50%, -50%)'
                          }}
                        >
                          {subTitleStr}
                        </span>
                      )}

                      {/* Heading */}
                      {!b.hideDiscount && titleStr && (
                        <h3
                          className="absolute text-white font-black text-2xl drop-shadow-[0_4px_10px_rgba(0,0,0,0.8)] leading-none tracking-tighter whitespace-nowrap z-20 transition-all duration-700"
                          style={{
                            left: `${b.headingPosition?.x || 10}%`,
                            top: `${b.headingPosition?.y || 52}%`,
                            transform: 'translate(-50%, -50%)',
                            fontSize: 'clamp(1.5rem, 6vw, 6rem)'
                          }}
                        >
                          {titleStr}
                        </h3>
                      )}

                      {/* Button */}
                      {!b.hideButton && btnStr && linkStr && (
                        <div
                          className="absolute pointer-events-auto"
                          style={{
                            left: `${b.buttonPosition?.x || 50}%`,
                            top: `${b.buttonPosition?.y || 80}%`,
                            transform: 'translate(-50%, -50%)'
                          }}
                        >
                          <a
                            href={linkStr}
                            onClick={(e) => isDraggingBanner ? e.preventDefault() : null}
                            className="bg-white text-slate-900 px-4 md:px-8 py-2 md:py-3 rounded-full font-black text-[10px] sm:text-xs md:text-sm shadow-[0_10px_20px_rgba(255,255,255,0.2)] hover:scale-105 active:scale-95 transition-all flex items-center gap-2 whitespace-nowrap group/btn"
                          >
                            {btnStr}
                            <ArrowRight size={16} className="hidden sm:block transition-transform group-hover/btn:translate-x-1" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Indicators */}
            {activeBanners.length > 1 && (
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5 md:gap-2 z-40">
                {activeBanners.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setCurrentBannerIndex(i); }}
                    className={`h-1.5 md:h-2 rounded-full transition-all duration-300 ${currentBannerIndex === i ? 'bg-white w-6 md:w-10' : 'bg-white/30 hover:bg-white/60 w-1.5 md:w-2'}`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Navigation Arrows */}
            {activeBanners.length > 1 && (
              <div className="absolute inset-0 z-30 hidden md:flex items-center justify-between px-2 md:px-4 pointer-events-none opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300">
                <button
                  onClick={(e) => { e.stopPropagation(); handlePrevSlide(); }}
                  className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center text-white transition-all active:scale-90 pointer-events-auto"
                >
                  <ChevronRight size={24} className="md:w-8 md:h-8" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleNextSlide(); }}
                  className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center text-white transition-all active:scale-90 pointer-events-auto"
                >
                  <ChevronLeft size={24} className="md:w-8 md:h-8" />
                </button>
              </div>
            )}
          </div>
        )}    <div className="w-full flex items-center justify-center mb-6 shrink-0">
          <h2 className={`text-2xl md:text-4xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>{sectionTitle}</h2>
        </div>

        <div className="flex-1 overflow-y-auto w-full px-4 md:px-6 py-6 rounded-[2rem] border-transparent custom-scrollbar max-h-[60vh] mb-10">
          {loading || categoryLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-500 w-10 h-10" /></div>
          ) : (
            <div className="space-y-12">
              {pageConfig && pageConfig.sections && pageConfig.sections.length > 0 ? (
                // DYNAMIC SECTIONS BUILDER
                pageConfig.sections.sort((a, b) => a.order - b.order).map(section => {

                  // Filter products based on builder criteria
                  let sectionProducts = [];
                  if (section.filterCriteria.type === 'manual_ids') {
                    // Match specifically by product.id
                    sectionProducts = products.filter(p => section.filterCriteria.value.includes(p.id.toString()));
                  } else if (section.filterCriteria.type === 'subCategory_name' && section.filterCriteria.value.length > 0) {
                    const allowedKeywords = section.filterCriteria.value.map(v => v.trim().toLowerCase());
                    sectionProducts = products.filter(p => {
                      const searchStr = `${p.name} ${p.subCategory || ''} ${(p.showInSections || []).join(' ')}`.toLowerCase();
                      return allowedKeywords.some(kw => searchStr.includes(kw));
                    });
                  } else {
                    sectionProducts = [...products];
                  }

                  if (sectionProducts.length === 0) return null;

                  return (
                    <div key={section.id} className="w-full">
                      <h3 className={`text-xl md:text-2xl font-black mb-4 ${isDark ? 'text-white' : 'text-slate-800'}`} style={{ direction: 'rtl' }}>{section.title}</h3>
                      {section.displayMode === 'horizontal' ? (
                        <div className="flex gap-4 overflow-x-auto py-4 custom-scrollbar" style={{ direction: 'rtl' }}>
                          {sectionProducts.map((product, idx) => (
                            <ProductCardLoader
                              key={product.id}
                              product={product}
                              isDark={isDark}
                              onOpenDetails={onOpenDetails}
                              onAddToCart={onAddToCart}
                              isAdded={!!addedToCartMap[product.id as number]}
                              priority={idx < 4}
                              isRow={true}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" style={{ direction: 'rtl' }}>
                          {sectionProducts.map((product, idx) => (
                            <ProductCardLoader
                              key={product.id}
                              product={product}
                              isDark={isDark}
                              onOpenDetails={onOpenDetails}
                              onAddToCart={onAddToCart}
                              isAdded={!!addedToCartMap[product.id as number]}
                              priority={idx < 4}
                              isRow={false}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                // DEFAULT FALLBACK VIEW
                <>
                  {displayType === 'horizontal' ? (
                    <div className="flex gap-4 overflow-x-auto py-4 custom-scrollbar" style={{ direction: 'rtl' }}>
                      {displayProducts.map((product, idx) => (
                        <ProductCardLoader
                          key={product.id}
                          product={product}
                          isDark={isDark}
                          onOpenDetails={onOpenDetails}
                          onAddToCart={onAddToCart}
                          isAdded={!!addedToCartMap[product.id as number]}
                          priority={idx < 4}
                          isRow={true}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-8 w-full">
                      {displayProducts.map((p, idx) => (
                        <ProductCardLoader
                          key={p.id}
                          product={p}
                          isDark={isDark}
                          onOpenDetails={onOpenDetails}
                          onAddToCart={onAddToCart}
                          isAdded={addedToCartMap[p.id as number]}
                          priority={idx < 4}
                          isRow={false}
                        />
                      ))}
                    </div>
                  )}

                  {displayProducts.length === 0 && (
                    <div className="w-full flex flex-col items-center justify-center py-20 animate-fade-in">
                      <Package size={48} className={`mb-4 ${isDark ? 'text-slate-700' : 'text-slate-300'}`} />
                      <p className={`font-bold ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>لا توجد منتجات في هذا القسم حالياً</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
